/**
 * R2 / R3 in Michael Wallace's Redlining template (2026-10-18): one table for the institution, then one
 * for each assessment area containing MMCTs, each comparing non-MMCT (control) with MMCT. No loan
 * purpose. Same figures and colours as the Word export (fldMmctBlocks.ts).
 */

import clsx from 'clsx';

interface BlockCell {
  count: number;
  total: number;
  controlCount: number;
  controlTotal: number;
  hasDisparity: boolean;
  significanceLevel: string | null;
  noComparison?: boolean;
}

export interface MmctBlock {
  scope: 'INSTITUTION' | 'ASSESSMENT_AREA';
  assessmentAreaName: string | null;
  cell: BlockCell;
}

const pct = (count: number, total: number) => (total > 0 ? `${((count / total) * 100).toFixed(2)}%` : 'NA');

export function MmctBlocksTable({
  blocks,
  collateral,
  regulatorCode,
}: {
  blocks: MmctBlock[];
  /** R3 heads its columns "Collateral Denials / Total Denials". */
  collateral: boolean;
  regulatorCode?: string | null;
}) {
  const [lower, upper] = regulatorCode === 'FED' ? ['0-50.00%', '50.01-100%'] : ['0-49.99%', '50.00-100%'];
  const headers = collateral
    ? ['Minority Concentration', 'Collateral Denials', 'Total Denials', '%', 'Diff.', 'Sig.']
    : ['Minority Concentration', 'Denials', 'Applications', '%', 'Diff.', 'Sig.'];

  return (
    <div className="space-y-5">
      {blocks.map((b) => {
        const c = b.cell;
        const comparable = c.total > 0 && c.controlTotal > 0;
        const diff = comparable ? (c.count / c.total - c.controlCount / c.controlTotal) * 100 : null;
        const shownDiff = diff === null ? 'NA' : diff.toFixed(2);
        const tested = comparable && !c.noComparison && shownDiff !== '0.00' && shownDiff !== '-0.00';
        const sig = comparable ? c.significanceLevel ?? 'NA' : 'NA';
        const sigBad = sig === '<1%' || sig === '<5%';
        const sigWatch = sig === '<10%';
        return (
          <div key={`${b.scope}:${b.assessmentAreaName ?? ''}`}>
            <p className="text-sm font-semibold text-ink mb-2">
              {b.scope === 'INSTITUTION' ? 'Institution' : `Assessment Areas with MMCTs – ${b.assessmentAreaName}`}
            </p>
            <table className="min-w-full text-sm border border-rule">
              <thead className="bg-panel-2 text-ink-2">
                <tr>
                  {headers.map((h, i) => (
                    <th key={h} className={clsx('px-3 py-2 font-medium border-b border-rule', i === 0 ? 'text-left' : 'text-center')}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="tabular-nums">
                <tr className="border-b border-rule">
                  <td className="px-3 py-2">{lower}</td>
                  <td className="px-3 py-2 text-center">{c.controlCount.toLocaleString()}</td>
                  <td className="px-3 py-2 text-center">{c.controlTotal.toLocaleString()}</td>
                  <td className="px-3 py-2 text-center">{pct(c.controlCount, c.controlTotal)}</td>
                  <td className="px-3 py-2 text-center">-</td>
                  <td className="px-3 py-2 text-center">-</td>
                </tr>
                <tr>
                  <td className="px-3 py-2">{upper}</td>
                  <td className="px-3 py-2 text-center">{c.count.toLocaleString()}</td>
                  <td className="px-3 py-2 text-center">{c.total.toLocaleString()}</td>
                  <td className="px-3 py-2 text-center">{pct(c.count, c.total)}</td>
                  <td className={clsx('px-3 py-2 text-center', tested && 'font-semibold', tested && (c.hasDisparity ? 'text-red-700' : 'text-emerald-700'))}>
                    {shownDiff}
                  </td>
                  <td className={clsx('px-3 py-2 text-center', tested && 'font-semibold', tested && (sigBad ? 'text-red-700' : sigWatch ? 'text-orange-700' : 'text-emerald-700'))}>
                    {sig}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
